import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';
import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';

const LANGUAGE_OPTIONS = [
  { name: '한국어', value: 'ko' },
  { name: 'English', value: 'en' },
];

export default function LanguageSelectBox({ onChangeLanguage }) {
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { languageMode } = useLanguage();

  const [selectedValue, setSelectedValue] = useState(languageMode);
  const currentOption = LANGUAGE_OPTIONS.find(opt => opt.value === selectedValue) || LANGUAGE_OPTIONS[0];

  const [isClicked, setIsClicked] = useState(false);

  const TopLevelRef = useRef();
  const SelectRef = useRef();
  useEffect(() => {
    document.addEventListener('mousedown', onClickOutSide);
    return () => document.removeEventListener('mousedown', onClickOutSide);
  }, []);

  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
      SelectRef.current.style.border = '';
      SelectRef.current.style.height = '32px';
      SelectRef.current.style.overflow = 'hidden';
      setTimeout(() => setIsClicked(false), 150);
    }
  }

  function onClick() {
    if (isClicked) {
      SelectRef.current.style.border = '';
      SelectRef.current.style.height = '32px';
      SelectRef.current.style.overflow = 'hidden';
      setTimeout(() => setIsClicked(false), 150);
    }
else {
      SelectRef.current.style.border = `1px solid ${theme.primary}`;
      const filteredCount = LANGUAGE_OPTIONS.filter(opt => opt.value !== selectedValue).length;
      SelectRef.current.style.height = `${32 * (filteredCount + 1)}px`;
      SelectRef.current.style.overflowY = 'scroll';
      SelectRef.current.style.overflowX = 'none';
      setIsClicked(true);
    }
  }

  function optionalClick(index) {
    const filtered = LANGUAGE_OPTIONS.filter(opt => opt.value !== selectedValue);
    const newValue = filtered[index].value;
    setSelectedValue(newValue);
    onChangeLanguage?.(newValue);
    // previewLanguage(newValue);
    SelectRef.current.style.border = '';
    SelectRef.current.style.height = '32px';
    SelectRef.current.style.overflow = 'hidden';
    setTimeout(() => setIsClicked(false), 150);
  }

  return (
    <TopLevelWrapper ref={TopLevelRef}>
      <SelectBox ref={SelectRef} onClick={onClick}>
        <SelectedViewWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14}>{currentOption.name}</Text>
          <StyledArrowWrapper>
            <StyledArrow isClicked={isClicked} />
          </StyledArrowWrapper>
        </SelectedViewWrapper>
        {isClicked && LANGUAGE_OPTIONS.filter(opt => opt.value !== selectedValue).map((data, index) => (
          <OptionWrapper
            key={'LanguageOption-' + index}
            onClick={() => optionalClick(index)}>
            <Text
              fontFamily='NotoSansKR-Regular'
              fontSize={14}>
              {data.name}
            </Text>
          </OptionWrapper>
        ))}
      </SelectBox>
    </TopLevelWrapper>
  );
}

const WIDTH = 120;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: ${WIDTH}px;
  height: 32px;
`;

const SelectBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: ${WIDTH}px;
  height: 32px;

  border-radius: 8px;
  overflow: hidden;

  border: 1px solid ${props => props.theme.line};
  box-sizing: border-box;
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }

  &::-webkit-scrollbar {
    width: 0px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.primary};
    border: 3px solid transparent;
    height: 25px;
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
    border: 4px solid transparent;
    background-color: transparent;
  }
`;

const SelectedViewWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 3px 8px;

  width: calc(${WIDTH}px - 17px);

  border-bottom: 1px solid ${props => props.theme.line};
`;

const StyledArrowWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 15px;
  height: 25px;
`;

const StyledArrow = styled(LeftArrowSvg)`
  width: 10px;
  height: auto;
  flex-shrink: 0;

  color: ${props => props.theme.subText};
  transform: ${props => props.isClicked ? 'rotate(-90deg)' : 'rotate(90deg)'};
  transition: transform 0.3s ease-in-out;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 6px 8px;

  width: 100%;

  border-bottom: 1px solid ${props => props.theme.line};

  box-sizing: border-box;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};

      & p {
        color: ${props => props.theme.white};
      }
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.theme.text};

  margin: 0px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: all 0.3s ease-in-out;
`;
